// components/exercises/ExerciseBlocks.jsx
// Exercise rendering in the "Course Player.dc.html" design language: red task
// header strip, dark mac-style code windows, a try-it-yourself-first reveal
// row, and a blue "why" panel for the explanation.
//
// Two shapes share the same parts:
// - ExerciseBlock (default): one compact card, used under a video lesson.
// - ExerciseLesson: the dedicated exercise-page layout — separate "Your task",
//   "Why this exercise" and "Code flow · reference" cards, used by the player
//   for episodes with kind "exercise".
//
// Reveal behaviour is decided by the PRESENCE of solutionCode / expectedOutput /
// explanation on the exercise object, not by a separate locked prop. The API omits
// those fields when the user lacks access. When present, the button is a pure
// client-side toggle that reveals them inline (no refetch). When absent, the button
// reads "Unlock" and routes to the purchase flow.

import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const C = {
  ink: "#18181b",
  body: "#3f3f46",
  muted: "#71717a",
  soft: "#a1a1aa",
  line: "#e4e4e7",
  hover: "#f4f4f5",
  red: "#e11d48",
  redBg: "#fef2f2",
  redBorder: "#fecaca",
  redDark: "#991b1b",
  blue: "#2563eb",
  blueDark: "#1e40af",
  blueBg: "#eff6ff",
  blueBorder: "#bfdbfe",
  code: "#0d1117",
  cream: "#F5F2EC",
  orange: "#EA580C",
  orangeBg: "#fdf3ee",
  green: "#16A34A",
  greenBg: "#f0fdf4",
};

const mono = "'Space Mono', monospace";
const jmono = "'JetBrains Mono', monospace";
const sans = "'Plus Jakarta Sans', system-ui, sans-serif";
const serif = "'Fraunces', Georgia, serif";

// Per-type labels. Keys mirror the exercise.type contract.
const TYPES = {
  "code-challenge": "Code challenge",
  "predict-output": "Predict the output",
  "spot-bug": "Spot the bug",
  "fill-blank": "Fill in the blank",
  quiz: "Quick check",
};

function CodeWindow({ file, code }) {
  return (
    <div style={{ background: C.code, borderRadius: 8, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 16px", background: "rgba(255,255,255,0.04)", borderBottom: "0.5px solid rgba(255,255,255,0.08)" }}>
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#ff5f57" }} />
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#febc2e" }} />
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#28c840" }} />
        <span style={{ marginLeft: 10, fontFamily: jmono, fontSize: 11, color: "rgba(255,255,255,0.45)" }}>{file}</span>
      </div>
      <pre style={{ margin: 0, padding: "18px 22px", fontFamily: jmono, fontSize: 12.5, lineHeight: 1.8, color: "#d1d5db", overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {code}
      </pre>
    </div>
  );
}

// Shared payload parsing for both shapes.
function exerciseParts(exercise) {
  const payload = exercise.payload || {};
  const inputs = Array.isArray(payload.inputs) ? payload.inputs : [];
  const outputLines = Array.isArray(payload.outputLines) ? payload.outputLines : [];
  const rules = Array.isArray(payload.rules) ? payload.rules : [];
  const catchNote =
    payload.catch && typeof payload.catch === "object" ? payload.catch : null;
  const expectedForStarter =
    payload.expectedForStarter && typeof payload.expectedForStarter === "object"
      ? payload.expectedForStarter
      : null;
  return {
    requirements: Array.isArray(payload.requirements) ? payload.requirements : [],
    flow: Array.isArray(payload.flow) ? payload.flow : [],
    codeFile: payload.codeFile || "Solution",
    minutes: payload.estimatedMinutes,
    inputs,
    outputLines,
    rules,
    catchNote,
    expectedForStarter,
    // True when the exercise carries structured task keys. Legacy exercises
    // (only prompt and requirements) leave this false and render as before.
    hasStructuredTask:
      inputs.length > 0 || outputLines.length > 0 || rules.length > 0 ||
      catchNote != null || expectedForStarter != null,
    hasSolution:
      exercise.solutionCode != null ||
      exercise.expectedOutput != null ||
      exercise.explanation != null,
  };
}

const eyebrowStyle = (color) => ({
  fontFamily: jmono, fontSize: 10, fontWeight: 700,
  letterSpacing: "0.12em", textTransform: "uppercase", color,
});

// Structured task sub-blocks. Rendered in a fixed order when the payload
// carries structured task keys. Each key is optional; a missing key renders
// nothing. No shadows, no gradients.
const sectionLabelStyle = {
  fontFamily: jmono, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.12em",
  textTransform: "uppercase", color: C.muted, marginBottom: 10,
};
const tableStyle = {
  width: "100%", borderCollapse: "collapse", border: `1px solid ${C.line}`,
};
const thStyle = {
  fontFamily: jmono, fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
  textTransform: "uppercase", color: C.muted, textAlign: "left",
  padding: "9px 14px", borderBottom: `1px solid ${C.line}`,
};
const tdStyle = {
  fontFamily: sans, fontSize: 13.5, lineHeight: 1.6, color: C.body,
  padding: "9px 14px", verticalAlign: "top",
};
const chipStyle = {
  fontFamily: jmono, fontSize: 10.5, fontWeight: 500, color: C.muted,
  background: "#fff", border: `1px solid ${C.line}`, borderRadius: 4,
  padding: "3px 8px", whiteSpace: "nowrap",
};

function InputsTable({ rows }) {
  return (
    <div>
      <div style={sectionLabelStyle}>Inputs</div>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Name</th>
            <th style={thStyle}>Type</th>
            <th style={thStyle}>What it is</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderTop: `1px solid ${C.line}` }}>
              <td style={{ ...tdStyle, fontFamily: jmono, color: C.ink }}>{r.name}</td>
              <td style={{ ...tdStyle, fontFamily: jmono, color: C.muted }}>{r.type}</td>
              <td style={tdStyle}>{r.note}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OutputLinesTable({ rows }) {
  return (
    <div>
      <div style={sectionLabelStyle}>Output, exactly 3 lines in order</div>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Line</th>
            <th style={thStyle}>Prints</th>
            <th style={thStyle}>From</th>
            <th style={thStyle}>Construct</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderTop: `1px solid ${C.line}` }}>
              <td style={tdStyle}>{r.line}</td>
              <td style={{ ...tdStyle, fontFamily: jmono, color: C.ink }}>{r.prints}</td>
              <td style={tdStyle}>{r.source}</td>
              <td style={{ ...tdStyle, fontFamily: jmono, color: C.muted }}>{r.construct}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RulesGroup({ rules }) {
  return (
    <div>
      <div style={sectionLabelStyle}>The conditions</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        {rules.map((rule, i) => {
          const columns = Array.isArray(rule.columns) ? rule.columns : [];
          const rows = Array.isArray(rule.rows) ? rule.rows : [];
          return (
            <div key={i}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
                {rule.title && (
                  <span style={{ fontFamily: serif, fontSize: 16, fontWeight: 600, color: C.ink }}>{rule.title}</span>
                )}
                {rule.construct && <span style={chipStyle}>{rule.construct}</span>}
              </div>
              {(columns.length > 0 || rows.length > 0) && (
                <table style={tableStyle}>
                  {columns.length > 0 && (
                    <thead>
                      <tr>
                        {columns.map((col, ci) => (
                          <th key={ci} style={{ ...thStyle, background: C.cream, color: C.body }}>{col}</th>
                        ))}
                      </tr>
                    </thead>
                  )}
                  <tbody>
                    {rows.map((row, ri) => (
                      <tr key={ri} style={{ borderTop: `1px solid ${C.line}` }}>
                        {(Array.isArray(row) ? row : []).map((cell, ci) => (
                          <td key={ci} style={tdStyle}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CatchCallout({ note }) {
  return (
    <div style={{ borderLeft: `3px solid ${C.orange}`, background: C.orangeBg, borderRadius: 6, padding: "16px 20px" }}>
      {note.title && (
        <div style={{ fontFamily: sans, fontSize: 14.5, fontWeight: 700, color: C.ink, marginBottom: note.body || note.code ? 6 : 0 }}>
          {note.title}
        </div>
      )}
      {note.body && (
        <p style={{ margin: 0, maxWidth: "65ch", fontFamily: sans, fontSize: 14, lineHeight: 1.7, color: C.body }}>
          {note.body}
        </p>
      )}
      {note.code && (
        <div style={{ marginTop: 10, fontFamily: jmono, fontSize: 13, color: C.ink }}>{note.code}</div>
      )}
    </div>
  );
}

function ExpectedCallout({ note }) {
  return (
    <div style={{ borderLeft: `3px solid ${C.green}`, background: C.greenBg, borderRadius: 6, padding: "16px 20px" }}>
      {note.title && (
        <div style={{ fontFamily: sans, fontSize: 14.5, fontWeight: 700, color: C.ink, marginBottom: note.body || note.output ? 6 : 0 }}>
          {note.title}
        </div>
      )}
      {note.body && (
        <p style={{ margin: note.output ? "0 0 10px" : 0, maxWidth: "65ch", fontFamily: sans, fontSize: 14, lineHeight: 1.7, color: C.body }}>
          {note.body}
        </p>
      )}
      {note.output && (
        <pre style={{ margin: 0, fontFamily: jmono, fontSize: 13, lineHeight: 1.7, color: C.ink, whiteSpace: "pre", overflowX: "auto" }}>
          {note.output}
        </pre>
      )}
    </div>
  );
}

// Orchestrates the sub-blocks in the required order with clear vertical rhythm.
function StructuredTaskBlocks({ parts }) {
  const { inputs, outputLines, rules, catchNote, expectedForStarter } = parts;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {inputs.length > 0 && <InputsTable rows={inputs} />}
      {outputLines.length > 0 && <OutputLinesTable rows={outputLines} />}
      {rules.length > 0 && <RulesGroup rules={rules} />}
      {catchNote && <CatchCallout note={catchNote} />}
      {expectedForStarter && <ExpectedCallout note={expectedForStarter} />}
    </div>
  );
}

// Dedicated exercise-page layout ("Course Player.dc.html" exercise view).
export function ExerciseLesson({ exercise }) {
  const [revealed, setRevealed] = useState(false);
  const navigate = useNavigate();
  const { courseSlug } = useParams();
  const parts = exerciseParts(exercise);
  const { requirements, flow, codeFile, minutes, hasSolution, hasStructuredTask } = parts;

  const card = { background: "#fff", border: `1px solid ${C.line}`, borderRadius: 12, overflow: "hidden" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* Task */}
      <div style={card}>
        <div style={{ padding: "14px 28px", background: C.redBg, borderBottom: `1px solid ${C.redBorder}`, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={eyebrowStyle(C.red)}>✎ Your task</span>
          {minutes && (
            <span style={{ fontFamily: mono, fontSize: 10.5, color: C.redDark, marginLeft: "auto" }}>~{minutes}m</span>
          )}
        </div>
        <div style={{ padding: "24px 28px" }}>
          {exercise.prompt && (
            hasStructuredTask ? (
              <p style={{ margin: "0 0 24px", maxWidth: "65ch", fontFamily: sans, fontSize: 16.5, lineHeight: 1.7, color: C.ink }}>
                {exercise.prompt}
              </p>
            ) : (
              <p style={{ margin: requirements.length ? "0 0 16px" : 0, fontSize: 15, lineHeight: 1.8, color: C.ink }}>
                {exercise.prompt}
              </p>
            )
          )}
          {hasStructuredTask && (
            <div style={{ marginBottom: requirements.length ? 24 : 0 }}>
              <StructuredTaskBlocks parts={parts} />
            </div>
          )}
          {requirements.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {requirements.map((req, i) => (
                <div key={i} style={{ display: "flex", gap: 10, fontSize: 14, lineHeight: 1.6, color: C.body }}>
                  <span style={{ color: C.red, flexShrink: 0 }}>→</span>
                  <span>{req}</span>
                </div>
              ))}
            </div>
          )}
          {exercise.starterCode && (
            <div style={{ marginTop: 16 }}>
              <CodeWindow file="Starter" code={exercise.starterCode} />
            </div>
          )}
        </div>
      </div>

      {/* Why this exercise */}
      {exercise.explanation && (
        <div style={card}>
          <div style={{ padding: "14px 28px", background: C.blueBg, borderBottom: `1px solid ${C.blueBorder}` }}>
            <span style={eyebrowStyle(C.blueDark)}>Why this exercise</span>
          </div>
          <div style={{ padding: "24px 28px" }}>
            <p style={{ margin: 0, fontSize: 15, fontWeight: 300, lineHeight: 1.8, color: C.body }}>
              {exercise.explanation}
            </p>
          </div>
        </div>
      )}

      {/* Hidden code flow */}
      <div style={card}>
        <button
          onClick={() =>
            hasSolution ? setRevealed((r) => !r) : navigate(`/courses/${courseSlug}`)
          }
          style={{
            width: "100%", padding: "16px 28px",
            background: revealed ? C.hover : "#fff",
            border: "none", cursor: "pointer",
            display: "flex", alignItems: "center", gap: 12,
            textAlign: "left", fontFamily: "'DM Sans', sans-serif", transition: "background .15s",
          }}
        >
          <span style={{ ...eyebrowStyle(C.muted), whiteSpace: "nowrap" }}>Code flow · reference</span>
          <span style={{ fontSize: 12.5, color: C.soft, flex: 1, minWidth: 120 }}>
            {hasSolution
              ? "Try it yourself first — then peek."
              : "Unlock the course to see the reference solution."}
          </span>
          <span style={{ fontFamily: mono, fontSize: 11, color: C.blue, whiteSpace: "nowrap" }}>
            {hasSolution ? (revealed ? "Hide ↑" : "Reveal ↓") : "Unlock →"}
          </span>
        </button>
        {hasSolution && revealed && (
          <div style={{ padding: "24px 28px", borderTop: `1px solid ${C.line}`, display: "flex", flexDirection: "column", gap: 22 }}>
            {flow.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {flow.map((step, i) => (
                  <div key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                    <span style={{ width: 22, height: 22, borderRadius: "50%", background: C.blueBg, color: C.blueDark, border: `1px solid ${C.blueBorder}`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: jmono, fontSize: 10, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>
                      {i + 1}
                    </span>
                    <span style={{ fontSize: 14, lineHeight: 1.65, color: C.body }}>{step}</span>
                  </div>
                ))}
              </div>
            )}
            {exercise.solutionCode && <CodeWindow file={codeFile} code={exercise.solutionCode} />}
            {exercise.expectedOutput && <CodeWindow file="Expected output" code={exercise.expectedOutput} />}
          </div>
        )}
      </div>
    </div>
  );
}

function ExerciseBlock({ exercise }) {
  const [revealed, setRevealed] = useState(false);
  const navigate = useNavigate();
  const { courseSlug } = useParams();

  const label = TYPES[exercise.type] || TYPES["code-challenge"];
  const parts = exerciseParts(exercise);
  const { requirements, flow, codeFile, minutes, hasSolution, hasStructuredTask } = parts;

  function onReveal() {
    if (hasSolution) {
      setRevealed((r) => !r);
    } else {
      // The enrollment page is the purchase flow for this course.
      navigate(`/courses/${courseSlug}`);
    }
  }

  return (
    <div style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: 12, overflow: "hidden", margin: "16px 0" }}>

      {/* Task header */}
      <div style={{ padding: "14px 28px", background: C.redBg, borderBottom: `1px solid ${C.redBorder}`, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontFamily: jmono, fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: C.red }}>
          ✎ {exercise.title}
        </span>
        <span style={{ fontFamily: mono, fontSize: 10.5, color: C.redDark, marginLeft: "auto" }}>
          {minutes ? `${label} · ~${minutes}m` : label}
        </span>
      </div>

      {/* Prompt + structured task detail + requirements + starter code */}
      <div style={{ padding: "22px 28px" }}>
        {exercise.prompt && (
          hasStructuredTask ? (
            <p style={{ margin: "0 0 24px", maxWidth: "65ch", fontFamily: sans, fontSize: 16.5, lineHeight: 1.7, color: C.ink }}>
              {exercise.prompt}
            </p>
          ) : (
            <p style={{ margin: requirements.length || exercise.starterCode ? "0 0 16px" : 0, fontSize: 15, lineHeight: 1.8, color: C.ink }}>
              {exercise.prompt}
            </p>
          )
        )}
        {hasStructuredTask && (
          <div style={{ marginBottom: requirements.length || exercise.starterCode ? 24 : 0 }}>
            <StructuredTaskBlocks parts={parts} />
          </div>
        )}
        {requirements.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: exercise.starterCode ? 16 : 0 }}>
            {requirements.map((req, i) => (
              <div key={i} style={{ display: "flex", gap: 10, fontSize: 14, lineHeight: 1.6, color: C.body }}>
                <span style={{ color: C.red, flexShrink: 0 }}>→</span>
                <span>{req}</span>
              </div>
            ))}
          </div>
        )}
        {exercise.starterCode && <CodeWindow file="Starter" code={exercise.starterCode} />}
      </div>

      {/* Hidden solution flow */}
      <button
        onClick={onReveal}
        style={{
          width: "100%", padding: "16px 28px",
          background: revealed ? C.hover : "#fff",
          border: "none", borderTop: `1px solid ${C.line}`,
          cursor: "pointer", display: "flex", alignItems: "center", gap: 12,
          textAlign: "left", fontFamily: "'DM Sans', sans-serif", transition: "background .15s",
        }}
      >
        <span style={{ fontFamily: jmono, fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: C.muted, whiteSpace: "nowrap" }}>
          Solution · reference
        </span>
        <span style={{ fontSize: 12.5, color: C.soft, flex: 1, minWidth: 120 }}>
          {hasSolution
            ? "Try it yourself first — then peek."
            : "Unlock the course to see the solution."}
        </span>
        <span style={{ fontFamily: mono, fontSize: 11, color: C.blue, whiteSpace: "nowrap" }}>
          {hasSolution ? (revealed ? "Hide ↑" : "Reveal ↓") : "Unlock →"}
        </span>
      </button>

      {hasSolution && revealed && (
        <div style={{ padding: "22px 28px", borderTop: `1px solid ${C.line}`, display: "flex", flexDirection: "column", gap: 20 }}>
          {flow.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {flow.map((step, i) => (
                <div key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                  <span style={{ width: 22, height: 22, borderRadius: "50%", background: C.blueBg, color: C.blueDark, border: `1px solid ${C.blueBorder}`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: jmono, fontSize: 10, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>
                    {i + 1}
                  </span>
                  <span style={{ fontSize: 14, lineHeight: 1.65, color: C.body }}>{step}</span>
                </div>
              ))}
            </div>
          )}
          {exercise.solutionCode && <CodeWindow file={codeFile} code={exercise.solutionCode} />}
          {exercise.expectedOutput && <CodeWindow file="Expected output" code={exercise.expectedOutput} />}
          {exercise.explanation && (
            <div style={{ border: `1px solid ${C.blueBorder}`, borderRadius: 8, overflow: "hidden" }}>
              <div style={{ padding: "12px 22px", background: C.blueBg, borderBottom: `1px solid ${C.blueBorder}` }}>
                <span style={{ fontFamily: jmono, fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: C.blueDark }}>
                  Why this matters
                </span>
              </div>
              <div style={{ padding: "18px 22px" }}>
                <p style={{ margin: 0, fontSize: 14.5, fontWeight: 300, lineHeight: 1.8, color: C.body }}>
                  {exercise.explanation}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ExerciseBlock;
